import type { GameObj, KEventController } from 'kaplay'
import type { KAPLAYCtx } from '../types.ts'
import { useLang } from '../providers/i18n/i18n.ts'
import { getTiledSize } from '../utils.ts'
import { COLOR_HEX_PURPLE, COLOR_HEX_LIME } from '../consts.ts'

export function useGameListPopup(k: KAPLAYCtx) {
  const { getLangString } = useLang(k)

  let popupCloseButtonClickEvent: KEventController
  let popupTabButtonClickEvent: KEventController
  let objPopup: GameObj
  let objectContent: GameObj

  let currTab = 999

  k.loadSprite('popup', 'sprites/ui-popup-slice9.png', {
    slice9: {
      top: 16,
      bottom: 16,
      left: 16,
      right: 16,
    },
  })

  k.loadSprite('popupBtn', 'sprites/ui-popup-button.png', {
    sliceX: 1,
    sliceY: 2,
  })

  const popupSize = {
    w: getTiledSize(20),
    h: getTiledSize(9),
  }

  function getTabBtn(isActive: boolean) {
    return [
      k.sprite('popupBtn', {
        width: getTiledSize(2),
        height: getTiledSize(),
        frame: isActive ? 0 : 1,
      }),
      k.area(),
      k.fixed(),
      k.layer('ui'),
      'ui_popup_list_tab_btn',
      'ui_popup_list',
      'ui_popup',
      'ui',
    ]
  }

  function getTabBtnText(text: string, isActive: boolean) {
    return [
      k.text(text, {
        size: 14,
        font: 'Jersey10',
        lineSpacing: 0,
        width: getTiledSize(2),
        align: 'center',
      }),
      k.color(isActive ? k.Color.WHITE : k.Color.fromHex(COLOR_HEX_LIME)),
      k.fixed(),
      k.layer('ui'),
      'ui_popup_list_tab_btn_text',
      'ui_popup_list',
      'ui_popup',
      'ui',
    ]
  }

  function getTabContentText(text: string) {
    return [
      k.text(text, {
        size: 11,
        font: 'Jersey10',
        lineSpacing: 0,
      }),
      k.color(k.Color.fromHex(COLOR_HEX_PURPLE)),
      k.fixed(),
      k.layer('ui'),
      'ui_popup_list_text_content',
      'ui_popup_list',
      'ui_popup',
      'ui',
    ]
  }

  function showListPopup() {
    const halfWidth = k.width() / 2
    const halfHeight = k.height() / 2

    objPopup = k.add([])

    objPopup.add([
      k.rect(k.width(), k.height()),
      k.pos(0, 0),
      k.color(k.Color.fromHex(COLOR_HEX_PURPLE)),
      k.opacity(0.8),
      k.fixed(),
      k.layer('ui'),
      'ui_popup_backdrop',
      'ui_popup',
      'ui',
    ])

    objPopup.add([
      k.sprite('popup', {
        width: popupSize.w,
        height: popupSize.h,
      }),
      k.pos(halfWidth, halfHeight),
      k.anchor('center'),
      k.fixed(),
      k.layer('ui'),
      'ui_popup_background',
      'ui_popup',
      'ui',
    ])

    objPopup.add([
      k.sprite('ui', {
        width: getTiledSize(),
        height: getTiledSize(),
        anim: 'popupCloseBtn',
      }),
      k.pos(
        halfWidth + popupSize.w / 2 - getTiledSize(),
        halfHeight - popupSize.h / 2 + getTiledSize(),
      ),
      k.anchor('topright'),
      k.fixed(),
      k.area(),
      k.layer('ui'),
      'ui_popup_close_btn',
      'ui_popup',
      'ui',
    ])

    popupCloseButtonClickEvent = k.onClick('ui_popup_close_btn', () => {
      hideListPopup()
    })

    const titleTextOpts = {
      size: 14, // 8
      font: 'Jersey10',
      lineSpacing: -2,
      letterSpacing: -0.2,
    }

    const titleATextComp = k.text(getLangString('game_list_popup_text_title_a'), titleTextOpts)
    const titleBTextComp = k.text(getLangString('game_list_popup_text_title_b'), titleTextOpts)

    const { width: titleAWidth } = titleATextComp.renderArea()
    const { width: titleBWidth } = titleBTextComp.renderArea()
    const titleLeft = halfWidth - ((titleAWidth + titleBWidth) / 2)

    objPopup.add([
      titleATextComp,
      k.pos(titleLeft, halfHeight - 54),
      k.color(k.Color.fromHex(COLOR_HEX_PURPLE)),
      k.fixed(),
      k.layer('ui'),
      'ui_popup_text_title',
      'ui_popup',
      'ui',
    ])

    objPopup.add([
      titleBTextComp,
      k.pos(titleLeft+titleAWidth, halfHeight - 54),
      k.color(k.Color.fromHex(COLOR_HEX_LIME)),
      k.fixed(),
      k.layer('ui'),
      'ui_popup_text_title',
      'ui_popup',
      'ui',
    ])

    objectContent = objPopup.add([])

    function setTab(tab: number) {
      if (currTab === tab) return
      currTab = tab

      const textContentToDestroy = objectContent.get('ui_popup_list_text_content')
      textContentToDestroy.forEach((item) => k.destroy(item))

      const tabBtnsToDestroy = objPopup.get('ui_popup_list_tab_btn')
      tabBtnsToDestroy.forEach((item) => k.destroy(item))

      if (currTab === 0) {
        objectContent.add([
          ...getTabContentText(getLangString('game_list_popup_text_content_tab_0_a')),
          k.pos(halfWidth - 130, halfHeight - 12),
        ])

        objectContent.add([
          ...getTabContentText(getLangString('game_list_popup_text_content_tab_0_b')),
          k.pos(halfWidth + 30, halfHeight - 12),
        ])
      } else if (currTab === 1) {
        objectContent.add([
          ...getTabContentText(getLangString('game_list_popup_text_content_tab_1')),
          k.pos(halfWidth - 40, halfHeight - 4),
        ])
      } else if (currTab === 2) {
        objectContent.add([
          ...getTabContentText(getLangString('game_list_popup_text_content_tab_2')),
          k.pos(halfWidth - 40, halfHeight - 10),
        ])
      } else if (currTab === 3) {
        objectContent.add([
          ...getTabContentText(getLangString('game_list_popup_text_content_tab_3')),
          k.pos(halfWidth - 40, halfHeight - 4),
        ])
      } else if (currTab === 4) {
        objectContent.add([
          ...getTabContentText(getLangString('game_list_popup_text_content_tab_4')),
          k.pos(halfWidth - 40, halfHeight + 2),
        ])
      } else if (currTab === 5) {
        objectContent.add([
          ...getTabContentText(getLangString('game_list_popup_text_content_tab_5')),
          k.pos(halfWidth - 40, halfHeight - 4),
        ])
      } else if (currTab === 6) {
        objectContent.add([
          ...getTabContentText(getLangString('game_list_popup_text_content_tab_6')),
          k.pos(halfWidth - 40, halfHeight + 2),
        ])
      }

      // Tab Button 0
      objPopup.add([
        ...getTabBtn(currTab === 0),
        k.pos(halfWidth - getTiledSize(7), halfHeight - 36),
        'ui_popup_list_tab_btn_0',
      ])
      objPopup.add([
        ...getTabBtnText('ALL', currTab === 0),
        k.pos(halfWidth - getTiledSize(7), halfHeight - 35.5),
      ])

      // Tab Button 1
      objPopup.add([
        ...getTabBtn(currTab === 1),
        k.pos(halfWidth - getTiledSize(5), halfHeight - 36),
        'ui_popup_list_tab_btn_1',
      ])
      objPopup.add([
        ...getTabBtnText('08', currTab === 1),
        k.pos(halfWidth - getTiledSize(5), halfHeight - 35.5),
      ])

      // Tab Button 2
      objPopup.add([
        ...getTabBtn(currTab === 2),
        k.pos(halfWidth - getTiledSize(3), halfHeight - 36),
        'ui_popup_list_tab_btn_2',
      ])
      objPopup.add([
        ...getTabBtnText('09', currTab === 2),
        k.pos(halfWidth - getTiledSize(3), halfHeight - 35.5),
      ])

      // Tab Button 3
      objPopup.add([
        ...getTabBtn(currTab === 3),
        k.pos(halfWidth - getTiledSize(1), halfHeight - 36),
        'ui_popup_list_tab_btn_3',
      ])
      objPopup.add([
        ...getTabBtnText('10', currTab === 3),
        k.pos(halfWidth - getTiledSize(1), halfHeight - 35.5),
      ])

      // Tab Button 4
      objPopup.add([
        ...getTabBtn(currTab === 4),
        k.pos(halfWidth + getTiledSize(1), halfHeight - 36),
        'ui_popup_list_tab_btn_4',
      ])
      objPopup.add([
        ...getTabBtnText('11', currTab === 4),
        k.pos(halfWidth + getTiledSize(1), halfHeight - 35.5),
      ])

      // Tab Button 5
      objPopup.add([
        ...getTabBtn(currTab === 5),
        k.pos(halfWidth + getTiledSize(3), halfHeight - 36),
        'ui_popup_list_tab_btn_5',
      ])
      objPopup.add([
        ...getTabBtnText('12', currTab === 5),
        k.pos(halfWidth + getTiledSize(3), halfHeight - 35.5),
      ])

      // Tab Button 6
      objPopup.add([
        ...getTabBtn(currTab === 6),
        k.pos(halfWidth + getTiledSize(5), halfHeight - 36),
        'ui_popup_list_tab_btn_6',
      ])
      objPopup.add([
        ...getTabBtnText('13', currTab === 6),
        k.pos(halfWidth + getTiledSize(5), halfHeight - 35.5),
      ])
    }

    popupTabButtonClickEvent = k.onClick('ui_popup_list_tab_btn', (a: GameObj) => {
      if (a.tags.includes('ui_popup_list_tab_btn_0')) setTab(0)
      else if (a.tags.includes('ui_popup_list_tab_btn_1')) setTab(1)
      else if (a.tags.includes('ui_popup_list_tab_btn_2')) setTab(2)
      else if (a.tags.includes('ui_popup_list_tab_btn_3')) setTab(3)
      else if (a.tags.includes('ui_popup_list_tab_btn_4')) setTab(4)
      else if (a.tags.includes('ui_popup_list_tab_btn_5')) setTab(5)
      else if (a.tags.includes('ui_popup_list_tab_btn_6')) setTab(6)
    })

    setTab(0)
  }

  function hideListPopup() {
    popupCloseButtonClickEvent.cancel()
    popupTabButtonClickEvent.cancel()
    k.destroy(objPopup)
    currTab = 999
  }

  return {
    showListPopup,
    hideListPopup,
  }
}
